import { useEffect, useState } from "react";
import { AlertCircle, Info, Loader2, ThumbsDown, ThumbsUp } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  fetchWorkspaceTaskPerformanceMetrics,
  fetchWorkspaceTaskPerformanceSummary,
  memberHasMetricActivity,
  periodToMetricDates,
  type TaskPerformanceMetricRow,
  type TaskPerformanceSummary,
  type TaskMetricsPeriod,
} from "@/lib/task-metrics";
import { TaskPerformanceSummaryCards } from "@/components/tasks/TaskPerformanceSummaryCards";
import { TaskPerformanceMemberChart } from "@/components/tasks/TaskPerformanceMemberChart";
import { TasksCompletionArc } from "@/components/tasks/TasksCompletionArc";

function MetricStat({
  label,
  value,
  variant = "default",
}: {
  label: string;
  value: number | string;
  variant?: "default" | "danger" | "success";
}) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2 min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
        {label}
      </p>
      <p
        className={cn(
          "text-lg font-semibold tabular-nums mt-0.5",
          variant === "danger" && "text-destructive",
          variant === "success" && "text-emerald-600",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function MemberMetricsCard({ row }: { row: TaskPerformanceMetricRow }) {
  const name = row.display_name || "(sem nome)";

  return (
    <Card className="p-4 space-y-3 md:hidden">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-base leading-tight">{name}</p>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase text-muted-foreground">Conclusão</p>
          <p className="text-xl font-bold tabular-nums text-primary">
            {row.completion_rate.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MetricStat label="Total" value={row.total_assigned} />
        <MetricStat label="Concluídas" value={row.completed} variant="success" />
        <MetricStat label="Pendentes" value={row.pending} />
        <MetricStat
          label="Atrasadas"
          value={row.overdue}
          variant={row.overdue > 0 ? "danger" : "default"}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">Tempo médio: </span>
          <span className="font-medium tabular-nums">
            {row.avg_completion_days != null
              ? `${row.avg_completion_days.toFixed(1)}d`
              : "—"}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Risco: </span>
          <span
            className={cn(
              "font-medium tabular-nums",
              row.delay_risk_index >= 50 && "text-amber-600",
              row.delay_risk_index >= 75 && "text-destructive",
            )}
          >
            {row.delay_risk_index.toFixed(0)}/100
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">% atraso: </span>
          <span className="font-medium tabular-nums">
            {row.overdue_rate_snapshot.toFixed(1)}%
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Subtarefas: </span>
          <span className="font-medium tabular-nums">
            {row.subtask_completion_rate != null
              ? `${row.subtask_completion_rate.toFixed(0)}%`
              : "—"}
          </span>
        </div>
      </div>

      <div className="rounded-lg border bg-background p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Resultado</p>
        <div className="flex items-center justify-around gap-4">
          <div className="flex flex-col items-center gap-1 min-w-[4.5rem]">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold text-lg">
              <ThumbsUp className="h-5 w-5" />
              {row.positive_count}
            </span>
            <span className="text-[10px] text-muted-foreground">Positivo</span>
          </div>
          <div className="w-px h-10 bg-border" />
          <div className="flex flex-col items-center gap-1 min-w-[4.5rem]">
            <span className="inline-flex items-center gap-1.5 text-rose-600 font-semibold text-lg">
              <ThumbsDown className="h-5 w-5" />
              {row.negative_count}
            </span>
            <span className="text-[10px] text-muted-foreground">Negativo</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ColumnHeader({
  label,
  tooltip,
  className,
}: {
  label: string;
  tooltip: string;
  className?: string;
}) {
  return (
    <TableHead className={className}>
      <div className="inline-flex items-center justify-end gap-1">
        <span>{label}</span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground hover:text-foreground">
                <Info className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </TableHead>
  );
}

interface TaskMetricsPanelProps {
  workspaceId: string;
  projectId?: string | null;
  projects?: { id: string; name: string }[];
  members?: { id: string; display_name: string | null }[];
}

export function TaskMetricsPanel({
  workspaceId,
  projectId = null,
  projects = [],
  members = [],
}: TaskMetricsPanelProps) {
  const [metricsProjectId, setMetricsProjectId] = useState<string | null>(projectId);
  const [period, setPeriod] = useState<TaskMetricsPeriod>("7d");
  const [includeSubtasks, setIncludeSubtasks] = useState(true);
  const [rows, setRows] = useState<TaskPerformanceMetricRow[]>([]);
  const [summary, setSummary] = useState<TaskPerformanceSummary | null>(null);
  const [metricDates, setMetricDates] = useState(() => periodToMetricDates("7d"));
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    setMetricsProjectId(projectId ?? null);
  }, [workspaceId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { from, to } = periodToMetricDates(period);
        setMetricDates({ from, to });
        const [metrics, summaryData] = await Promise.all([
          fetchWorkspaceTaskPerformanceMetrics(
            workspaceId,
            from,
            to,
            includeSubtasks,
            metricsProjectId,
          ),
          fetchWorkspaceTaskPerformanceSummary(workspaceId, from, to, metricsProjectId),
        ]);
        setRows(metrics.filter(memberHasMetricActivity));
        setSummary(summaryData);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setLoadError(message);
        toast({
          title: "Erro ao carregar métricas",
          description: message,
          variant: "destructive",
        });
        setRows([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [workspaceId, period, includeSubtasks, metricsProjectId, toast]);

  return (
    <Card className="p-3 sm:p-4 space-y-4">
      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold">Desempenho da equipe</h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Conclusões pela data em que foram marcadas. Tarefas futuras concluídas antes do prazo
            entram nas métricas. Atraso considera prazo do dia com horário quando definido.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 w-full">
          {(["today", "7d", "30d"] as TaskMetricsPeriod[]).map((p) => (
            <Button
              key={p}
              size="sm"
              className="w-full text-xs sm:text-sm"
              variant={period === p ? "default" : "outline"}
              onClick={() => setPeriod(p)}
            >
              {p === "today" ? "Hoje" : p === "7d" ? "7 dias" : "30 dias"}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="include-subtasks"
            checked={includeSubtasks}
            onCheckedChange={setIncludeSubtasks}
          />
          <Label htmlFor="include-subtasks" className="text-sm cursor-pointer">
            Incluir subtarefas nas métricas
          </Label>
        </div>
        {projects.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Projeto</Label>
            <Select
              value={metricsProjectId ?? "all"}
              onValueChange={(value) =>
                setMetricsProjectId(value === "all" ? null : value)
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos os projetos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os projetos</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Não foi possível carregar as métricas</AlertTitle>
          <AlertDescription className="text-sm">{loadError}</AlertDescription>
        </Alert>
      )}

      {summary && !loading && (
        <div className="rounded-2xl border bg-muted/20 px-3 py-4 md:hidden">
          <p className="text-sm font-semibold text-center mb-1">Conclusão no período</p>
          <TasksCompletionArc
            completed={summary.completed}
            pending={summary.pending}
            overdue={summary.overdue}
          />
        </div>
      )}

      <TaskPerformanceSummaryCards summary={summary} loading={loading} />

      {metricDates.from && metricDates.to && (
        <TaskPerformanceMemberChart
          workspaceId={workspaceId}
          from={metricDates.from}
          to={metricDates.to}
          rows={rows}
          members={members}
          loading={loading}
          projectId={metricsProjectId}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nenhuma atividade neste período. Conclua tarefas na aba Equipe ou amplie o filtro de tempo.
        </p>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {rows.map((r) => (
              <MemberMetricsCard key={r.member_id} row={r} />
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto -mx-1 px-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Membro</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Concl.</TableHead>
                  <TableHead className="text-right">Pend.</TableHead>
                  <TableHead className="text-right">Atras.</TableHead>
                  <ColumnHeader
                    label="Tempo"
                    tooltip="Média de dias entre criação e conclusão no período."
                    className="text-right"
                  />
                  <ColumnHeader
                    label="% Atraso"
                    tooltip="Atrasadas ÷ (atrasadas + pendentes em aberto)."
                    className="text-right"
                  />
                  <ColumnHeader
                    label="Risco"
                    tooltip="Índice 0–100 combinando atrasos, prazos próximos e histórico."
                    className="text-right"
                  />
                  <ColumnHeader
                    label="Subt."
                    tooltip="Taxa de conclusão de subtarefas do membro no período."
                    className="text-right"
                  />
                  <TableHead className="text-right">Conc.%</TableHead>
                  <TableHead className="text-right">Resultado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.member_id}>
                    <TableCell className="font-medium">
                      {r.display_name || "(sem nome)"}
                    </TableCell>
                    <TableCell className="text-right">{r.total_assigned}</TableCell>
                    <TableCell className="text-right">{r.completed}</TableCell>
                    <TableCell className="text-right">
                      {r.pending}
                      {r.pending_subtasks > 0 && (
                        <span className="text-[10px] text-muted-foreground ml-1">
                          ({r.pending_subtasks} st)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.overdue > 0 ? (
                        <span className="text-destructive font-medium">{r.overdue}</span>
                      ) : (
                        r.overdue
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.avg_completion_days != null
                        ? `${r.avg_completion_days.toFixed(1)}d`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.overdue_rate_snapshot.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span
                        className={cn(
                          r.delay_risk_index >= 50 && "text-amber-600 font-medium",
                          r.delay_risk_index >= 75 && "text-destructive font-medium",
                        )}
                      >
                        {r.delay_risk_index.toFixed(0)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.subtask_completion_rate != null
                        ? `${r.subtask_completion_rate.toFixed(0)}%`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {r.completion_rate.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <ThumbsUp className="h-3 w-3" />
                          {r.positive_count}
                        </span>
                        <span className="inline-flex items-center gap-1 text-rose-600">
                          <ThumbsDown className="h-3 w-3" />
                          {r.negative_count}
                        </span>
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}
